import { ArrowRight2, CloseCircle, Profile, Verify } from "iconsax-react";
import {
	MDBCard,
	MDBCardBody,
	MDBCardText,
	MDBCardTitle,
} from "mdb-react-ui-kit";
import "./style.css";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import arrowRight from "../../assets/images/arrow-right.png";
import personIcon from "../../assets/images/brad_transparent.png";
import profileCircle from "../../assets/images/profile-circle.png";
import { updateUserManagerStatus } from "../../Store/UserAction";
import { api } from "../../utils/axios";
import AddTeamMember from "../addTeamMemeber/AddTeamMember";
import RemoveUserConfirmModal from "../../components/user/RemoveUserConfirmModal.tsx";

// Custom ThreeDots component
const ThreeDots = ({ size = 20, color = "#0052CC" }) => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 20 20"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<circle cx="4" cy="10" r="2" stroke={color} strokeWidth="1.5" fill="none" />
		<circle
			cx="10"
			cy="10"
			r="2"
			stroke={color}
			strokeWidth="1.5"
			fill="none"
		/>
		<circle
			cx="16"
			cy="10"
			r="2"
			stroke={color}
			strokeWidth="1.5"
			fill="none"
		/>
	</svg>
);

function UsersInfo({ user, mbti, mbtiDescriptions, team_users }) {
	const [teamOpen, setTeamOpen] = useState(true);
	const [showAddMember, setShowAddMember] = useState(false);
	const [hoveredUserId, setHoveredUserId] = useState(null);
	const [promotingUserId, setPromotingUserId] = useState(null);
	const [removingUser, setRemovingUser] = useState(null);
	const [showMBTIDetails, setShowMBTIDetails] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	const mbtiDetailsRef = useRef(null);
	const userData = useSelector((state) => state.user.data);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Use Redux store data instead of prop for team_users to ensure updates are reflected
	const teamUsersFromStore = userData?.team_users || [];
	const teamUsers = team_users || teamUsersFromStore;

	// Click navigation handlers
	const handleLeftClick = () => {
		if (currentSlide > 0) {
			setCurrentSlide(currentSlide - 1);
		}
	};

	const handleRightClick = () => {
		if (currentSlide < 2) {
			setCurrentSlide(currentSlide + 1);
		}
	};

	// Helper for Figma-style MBTI label (Farsi)
	const getMbtiLabel = (type) => mbtiDescriptions[type]?.split(":")[0] || "";
	// For styling check only: set a fake MBTI if not provided
	const displayMbti = mbti || "ENTP";

	// Helper function to get profile picture with fallback
	const getProfilePicture = (userData, fallbackImage) => {
		if (userData?.profile_picture) {
			return userData.profile_picture;
		}
		return fallbackImage;
	};

	// Subscription data from backend
	const subscription = userData?.subscription;
	const currentMembers = teamUsers?.length || 0;
	const maxMembers = subscription?.max_members || 5;
	const currentPlan = subscription?.name || "رایگان";

	// Calculate progress percentage
	const progressPercentage = Math.min((currentMembers / maxMembers) * 100, 100);

	// Determine progress bar color
	const getProgressBarColor = () => {
		if (progressPercentage >= 90) return "danger";
		if (progressPercentage >= 70) return "warning";
		return "";
	};

	// Check if user can add more members
	const canAddMembers = currentMembers < maxMembers;

	// Handle add member button click
	const handleAddMember = () => {
		if (!canAddMembers) {
			// Show subscription upgrade modal or navigate to subscription page
			navigate("/userProfile");
			return;
		}
		setShowAddMember(true);
	};

	// Handle upgrade subscription
	const handleUpgradeSubscription = () => {
		navigate("/userProfile");
	};

	// Check if current user is a manager
	const isCurrentUserManager = userData?.user?.is_leader;

	// MBTI details rendering logic
	const renderMBTIDetails = () => {
		if (!selectedUser) return null;

		// Check if user has completed MBTI test
		if (!selectedUser.mbti_percentages) {
			return (
				<div className="mbti-details-container" ref={mbtiDetailsRef}>
					{/* Header */}
					<div className="mbti-details-header">
						<button
							className="mbti-details-back-btn"
							onClick={handleCloseMBTIDetails}
						>
							<ArrowRight2 size={20} variant="Linear" />
							بازگشت به منو
						</button>
					</div>

					{/* Content */}
					<div className="mbti-details-content">
						<div className="mbti-details-slide">
							<div className="mbti-no-test-content">
								<div className="mbti-no-test-icon">
									<Profile size={48} variant="Linear" color="#6c757d" />
								</div>
								<h2 className="mbti-no-test-title">آزمون MBTI تکمیل نشده</h2>
								<p className="mbti-no-test-message">
									{selectedUser.fname} {selectedUser.lname} هنوز آزمون
									شخصیت‌شناسی MBTI را تکمیل نکرده است.
								</p>
								<p className="mbti-no-test-submessage">
									برای مشاهده جزئیات شخصیتی، ابتدا باید آزمون MBTI را تکمیل
									کنید.
								</p>
							</div>
						</div>
					</div>
				</div>
			);
		}

		const mbtiType = selectedUser.mbti || "ENTP";

		// Color mapping for MBTI types
		const mbtiColors = {
			ENTP: { main: "#7C5DC5", light: "#E9D8FD" },
			ISTP: { main: "#F5B000", light: "#FFF6D6" },
			INFJ: { main: "#1B9E77", light: "#D6F5EC" },
			ESTJ: { main: "#3C9C99", light: "#D6F5F2" },
		};

		const typeColor = mbtiColors[mbtiType] || mbtiColors.ENTP;

		// MBTI dimension pairs
		const mbtiPairs = [
			{
				left: { label: "درونگرا(I)", code: "I" },
				right: { label: "برونگرا(E)", code: "E" },
				title: "نحوه تعامل با محیط اطراف",
			},
			{
				left: { label: "حسی(S)", code: "S" },
				right: { label: "شهودی(N)", code: "N" },
				title: "عامل هدایت انرژی ذهنی",
			},
			{
				left: { label: "احساسی(F)", code: "F" },
				right: { label: "منطقی(T)", code: "T" },
				title: "نحوه تصمیم گیری و واکنش به احساسات",
			},
			{
				left: { label: "قضاوت کننده(J)", code: "J" },
				right: { label: "اکتشاف کننده(P)", code: "P" },
				title: "رویکرد شخصی در کار و برنامه‌ریزی",
			},
		];

		const slides = [
			{
				id: "overview",
				title: `${selectedUser.fname} ${selectedUser.lname}`,
				subtitle: `شخصیت ${mbtiType} از گروه شخصیتی تحلیل گران`,
				content: (
					<div className="mbti-overview-slide">
						<div className="mbti-bars-container">
							{mbtiPairs.map((pair) => {
								const leftVal =
									selectedUser.mbti_percentages[pair.left.code] || 0;
								const rightVal =
									selectedUser.mbti_percentages[pair.right.code] || 0;
								const leftBold = leftVal > rightVal;
								const rightBold = rightVal > leftVal;

								return (
									<div
										key={pair.left.code + pair.right.code}
										className="mbti-pair-container"
										style={{ borderColor: typeColor.main }}
									>
										<div className="mbti-pair-title">{pair.title}</div>
										<div
											className="mbti-pair-bar-row"
											style={{ borderColor: typeColor.main }}
										>
											<div
												className={`mbti-pair-half mbti-pair-half-left ${leftBold ? "mbti-pair-half-bg" : "mbti-pair-half-white"}`}
												style={
													leftBold
														? { background: typeColor.main }
														: { background: "transparent" }
												}
											>
												<div
													className={`mbti-pair-circle mbti-pair-circle-left${leftBold ? " mbti-pair-circle-bold" : ""}`}
													style={
														leftBold
															? {
																	background: "#fff",
																	color: typeColor.main,
																	borderColor: typeColor.main,
																}
															: {
																	background: typeColor.light,
																	color: typeColor.main,
																	borderColor: typeColor.main,
																}
													}
												>
													<div className="mbti-pair-percent">
														{leftVal.toFixed(0)}%
													</div>
												</div>
												<div
													className={`mbti-pair-label mbti-pair-label-left${leftBold ? " mbti-pair-label-bold" : ""}`}
													style={
														leftBold
															? { color: "#fff" }
															: { color: typeColor.main }
													}
												>
													{pair.left.label}
												</div>
											</div>
											<div
												className={`mbti-pair-half mbti-pair-half-right ${rightBold ? "mbti-pair-half-bg" : "mbti-pair-half-white"}`}
												style={
													rightBold
														? { background: typeColor.main }
														: { background: "transparent" }
												}
											>
												<div
													className={`mbti-pair-label mbti-pair-label-right${rightBold ? " mbti-pair-label-bold" : ""}`}
													style={
														rightBold
															? { color: "#fff" }
															: { color: typeColor.main }
													}
												>
													{pair.right.label}
												</div>
												<div
													className={`mbti-pair-circle mbti-pair-circle-right${rightBold ? " mbti-pair-circle-bold" : ""}`}
													style={
														rightBold
															? {
																	background: "#fff",
																	color: typeColor.main,
																	borderColor: typeColor.main,
																}
															: {
																	background: typeColor.light,
																	color: typeColor.main,
																	borderColor: typeColor.main,
																}
													}
												>
													<div className="mbti-pair-percent">
														{rightVal.toFixed(0)}%
													</div>
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				),
			},
			{
				id: "interpretation",
				title: `تفسیر شخصیت ${mbtiType}`,
				content: (
					<div className="mbti-interpretation-slide">
						<div className="mbti-interpretation-content">
							{mbtiDescriptions && mbtiDescriptions[mbtiType] ? (
								<div className="mbti-description-text">
									{mbtiDescriptions[mbtiType]
										.split("\n")
										.map((paragraph, index) => (
											<p key={index}>{paragraph}</p>
										))}
								</div>
							) : (
								<p>توضیحی برای این تیپ شخصیتی موجود نیست.</p>
							)}
						</div>
					</div>
				),
			},
			{
				id: "strengths",
				title: `نقاط قوت شخصیتی ${mbtiType}`,
				content: (
					<div className="mbti-strengths-slide">
						<div className="mbti-strengths-content">
							<div className="strength-item">
								<h3>تفکر انتقادی و نوآوری</h3>
								<p>
									توانایی چالش کشیدن مفاهیم با دیدگاه‌های جدید و خلاقانه و ارائه
									راه‌حل‌های متفاوت.
								</p>
								<div className="strength-tip">
									<strong>راهکار شخصی:</strong> ثبت ایده‌های جدید در جلسات طوفان
									فکری و استفاده از روش‌های تفکر طراحی برای ساختاردهی افکار.
								</div>
							</div>
							<div className="strength-item">
								<h3>توانایی مجادله سازنده</h3>
								<p>
									توانایی هدایت بحث‌های نظری به سمت یافتن راه‌حل با استفاده از
									سوالات تیز و تحلیل‌های دقیق.
								</p>
								<div className="strength-tip">
									<strong>راهکار شخصی:</strong> تعریف هدف اصلی در بحث‌های تیمی و
									پرورش بحث‌های مفید با حفظ احترام متقابل.
								</div>
							</div>
						</div>
					</div>
				),
			},
		];

		return (
			<div className="mbti-details-container" ref={mbtiDetailsRef}>
				{/* Navigation Hint */}

				{/* Left Navigation Area */}
				{currentSlide > 0 && (
					<div className="mbti-nav-left" onClick={handleLeftClick}>
						<div className="mbti-nav-halo">
							<div className="mbti-nav-arrow">‹</div>
						</div>
					</div>
				)}

				{/* Right Navigation Area */}
				{currentSlide < 2 && (
					<div className="mbti-nav-right" onClick={handleRightClick}>
						<div className="mbti-nav-halo">
							<div className="mbti-nav-arrow">›</div>
						</div>
					</div>
				)}

				{/* Header */}
				<div className="mbti-details-header">
					<button
						className="mbti-details-back-btn"
						onClick={handleCloseMBTIDetails}
					>
						<ArrowRight2 size={20} variant="Linear" />
						بازگشت به منو
					</button>
				</div>

				{/* Slide Content */}
				<div className="mbti-details-content">
					<div className="mbti-details-slide">
						<h2 className="mbti-details-title">{slides[currentSlide].title}</h2>
						{slides[currentSlide].subtitle && (
							<p className="mbti-details-subtitle">
								{slides[currentSlide].subtitle}
							</p>
						)}
						<div className="mbti-details-body">
							{slides[currentSlide].content}
						</div>
					</div>
				</div>

				{/* Navigation */}
				<div className="mbti-details-navigation">
					<div className="mbti-details-bars">
						{slides.map((_, index) => (
							<div
								key={index}
								className={`mbti-details-bar ${index === currentSlide ? "active" : ""}`}
								onClick={() => setCurrentSlide(index)}
								style={{
									backgroundColor:
										index === currentSlide ? typeColor.main : "#e5e4e9",
								}}
							/>
						))}
					</div>
				</div>
			</div>
		);
	};

	// Handle promote to manager
	const handlePromoteToManager = async (userId) => {
		if (!isCurrentUserManager) {
			toast.error("فقط مدیران می‌توانند کاربران را ارتقا دهند");
			return;
		}

		setPromotingUserId(userId);
		try {
			const response = await api.post(
				"/leader/grant_manager",
				{
					user_id: userId,
				},
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
						"Content-Type": "application/json",
					},
				},
			);

			if (response.status === 201) {
				toast.success("کاربر با موفقیت به مدیر ارتقا یافت");
				// Update the user's manager status in Redux store
				dispatch(updateUserManagerStatus(userId, true));
			}
		} catch (error) {
			toast.error("خطا در ارتقای کاربر");
		} finally {
			setPromotingUserId(null);
		}
	};

	// Handle remove user
	const handleRemoveUser = async (user) => {
		if (!userData?.user?.is_manager) {
			toast.error("فقط مدیران می‌توانند کاربران را حذف کنند");
			return;
		}

		setRemovingUser(user);
	};

	// Handle MBTI details view
	const handleShowMBTIDetails = (user) => {
		setSelectedUser(user);
		setShowMBTIDetails(true);
		setCurrentSlide(0);
	};

	const handleCloseMBTIDetails = () => {
		setShowMBTIDetails(false);
		setSelectedUser(null);
		setCurrentSlide(0);
	};

	return (
		<div className="users-info-container">
			<div
				className="mbti-card-custom"
				onClick={() => handleShowMBTIDetails(user)}
				style={{ cursor: "pointer" }}
			>
				<div className="userinfo-avatar">
					<img src={getProfilePicture(user, profileCircle)} alt="avatar icon" />
				</div>
				<div className="userinfo-name">{user.fname + " " + user.lname}</div>
				<div className="userinfo-email">{user.email}</div>

				{user.mbti && (
					<div className="userinfo-mbti-card">
						<div className="userinfo-mbti-icon">
							{/* Replace with your actual MBTI SVG or image if needed */}
							<img src={personIcon} alt="person icon" />
							<div className="userinfo-mbti-type">{user.mbti}</div>
						</div>
						<div className="userinfo-mbti-desc">
							{mbtiDescriptions[user.mbti] ||
								"توضیحی برای این تیپ MBTI یافت نشد."}
						</div>
					</div>
				)}
			</div>

			{/* Subscription Info */}
			{subscription && (
				<div className="subscription-info">
					<div className="subscription-info-header">
						<span className="subscription-plan-name">{currentPlan}</span>
						{!canAddMembers && (
							<button
								className="subscription-upgrade-btn"
								onClick={handleUpgradeSubscription}
							>
								ارتقا
							</button>
						)}
					</div>
					<div className="subscription-details">
						<span className="subscription-members-count">
							{currentMembers} عضو
						</span>
						<span className="subscription-members-limit">
							از {maxMembers} عضو
						</span>
					</div>
					<div className="subscription-progress">
						<div
							className={`subscription-progress-bar ${getProgressBarColor()}`}
							style={{ width: `${progressPercentage}%` }}
						></div>
					</div>
				</div>
			)}

			{/* Team Users List - Mobile Accordion */}
			{removingUser ? (
				<RemoveUserConfirmModal
					user_id={removingUser?.uid}
					user_name={removingUser?.fname + " " + removingUser?.lname}
					setUserId={setRemovingUser}
				/>
			) : (
				<></>
			)}
			<div className="team-accordion mobile-only">
				<button
					className="team-toggle-btn"
					onClick={() => setTeamOpen(!teamOpen)}
				>
					<span>اعضای تیم</span>
					<img
						src={arrowRight}
						alt="arrow"
						className={`team-toggle-arrow${teamOpen ? " open" : ""}`}
						style={{
							width: 22,
							height: 22,
							marginLeft: 8,
							transition: "transform 0.25s",
						}}
					/>
				</button>
				<div
					className="team-collapsible-content"
					style={{
						maxHeight: teamOpen ? "390px" : "0",
						paddingBottom: teamOpen ? "20px" : "0",
						transition:
							"max-height 0.35s cubic-bezier(.4,0,.2,1), padding-bottom 0.2s",
						overflow: "hidden",
					}}
				>
					{showMBTIDetails && selectedUser ? (
						renderMBTIDetails()
					) : (
						<>
							<button
								className="users-team-add-btn"
								style={{
									width: "100%",
									margin: "18px 0",
									opacity: canAddMembers ? 1 : 0.6,
									cursor: canAddMembers ? "pointer" : "not-allowed",
								}}
								onClick={handleAddMember}
								disabled={!canAddMembers}
							>
								{canAddMembers ? "+ افزودن عضو" : "حد مجاز رسیده"}
							</button>
							<div className="users-team-list">
								{teamUsers &&
									teamUsers.length > 0 &&
									teamUsers.map((m, idx) => (
										<div
											key={m.uid}
											className={`users-team-row${idx % 2 === 1 ? " users-team-row-alt" : ""}`}
											onClick={() => handleShowMBTIDetails(m)}
											style={{ cursor: "pointer" }}
										>
											<div className="users-team-info">
												<div className="users-team-avatar">
													{m.profile_picture ? (
														<img
															src={m.profile_picture}
															alt={`${m.fname} ${m.lname}`}
															style={{
																width: "40px",
																height: "40px",
																minWidth: "40px",
																minHeight: "40px",
																borderRadius: "50%",
																objectFit: "cover",
															}}
														/>
													) : (
														<Profile
															size="40"
															color="#6B778C"
															variant="Linear"
														/>
													)}
												</div>
												<div className="users-team-meta">
													<span className="users-team-name">
														{m.fname + " " + m.lname}
													</span>
													<span className="users-team-mbti">
														{m.mbti && m.mbti + ": "}
														<span className="users-team-mbti-label">
															{m.mbti && getMbtiLabel(m.mbti)}
														</span>
													</span>
												</div>
												{m.is_team_leader ? (
													<span className="users-team-leader-icon">
														<Verify
															size={24}
															color="#0052CC"
															variant="Linear"
														/>
													</span>
												) : (
													<div
														className="users-team-ellipsis-container"
														onClick={(e) => {
															e.stopPropagation();
															setHoveredUserId(m.uid);
														}}
														onMouseLeave={() => {
															setHoveredUserId(null);
														}}
													>
														{(userData?.user?.is_leader ||
															userData?.user?.is_manager) && (
															<>
																<span className="users-team-ellipsis">
																	<ThreeDots size={24} color="#0052CC" />
																</span>
																{userData?.user?.is_leader && (
																	<div className="users-team-promotion-popup">
																		<button
																			className="users-team-promotion-btn"
																			onClick={(e) => {
																				e.stopPropagation();
																				handlePromoteToManager(m.uid);
																			}}
																			disabled={promotingUserId === m.uid}
																		>
																			{promotingUserId === m.uid
																				? "در حال ارتقا..."
																				: "ارتقای کاربر به نقش مدیر"}
																		</button>
																	</div>
																)}
																{userData?.user?.is_manager &&
																	m.uid !== userData?.user?.uid &&
																	!m.is_leader && (
																		<div className="users-team-promotion-popup">
																			<button
																				className="users-team-promotion-btn !tw-bg-red-500"
																				onClick={(e) => {
																					e.stopPropagation();
																					handleRemoveUser(m);
																				}}
																			>
																				حذف کاربر
																			</button>
																		</div>
																	)}
															</>
														)}
													</div>
												)}
											</div>
										</div>
									))}
							</div>
						</>
					)}
				</div>
			</div>
			{/* Team Users List - Figma style (desktop/tablet) */}
			{teamUsers && teamUsers.length > 0 && (
				<MDBCard className="users-team-card desktop-only">
					{showMBTIDetails && selectedUser ? (
						<div className="users-team-mbti-details">{renderMBTIDetails()}</div>
					) : (
						<>
							{/* Header */}
							<div className="users-team-header">
								<span className="users-team-title">اعضای تیم</span>
								<button
									className="users-team-add-btn"
									onClick={handleAddMember}
									disabled={!canAddMembers}
									style={{
										opacity: canAddMembers ? 1 : 0.6,
										cursor: canAddMembers ? "pointer" : "not-allowed",
									}}
								>
									{canAddMembers ? "+" : "!"}
								</button>
							</div>
							<MDBCardBody className="users-team-list">
								{teamUsers.map((m, idx) => (
									<div
										key={m.uid}
										className={`users-team-row${idx % 2 === 1 ? " users-team-row-alt" : ""}`}
										onClick={() => handleShowMBTIDetails(m)}
										style={{ cursor: "pointer" }}
									>
										{/* User info */}
										<div className="users-team-info">
											<div className="users-team-avatar">
												{m.profile_picture ? (
													<img
														src={m.profile_picture}
														alt={`${m.fname} ${m.lname}`}
														style={{
															width: "40px",
															height: "40px",
															minWidth: "40px",
															minHeight: "40px",
															borderRadius: "50%",
															objectFit: "cover",
														}}
													/>
												) : (
													<Profile size="40" color="#6B778C" variant="Linear" />
												)}
											</div>
											<div className="users-team-meta">
												<span className="users-team-name">
													{m.fname + " " + m.lname}
												</span>
												<span className="users-team-mbti">
													{m.mbti && m.mbti + ": "}
													<span className="users-team-mbti-label">
														{m.mbti && getMbtiLabel(m.mbti)}
													</span>
												</span>
											</div>
											{/* Leader icon or three dots icon */}
											{m.is_team_leader ? (
												<span className="users-team-leader-icon">
													<Verify size={24} color="#0052CC" variant="Linear" />
												</span>
											) : (
												<div
													className="users-team-ellipsis-container"
													onMouseEnter={() => {
														setHoveredUserId(m.uid);
													}}
													onMouseLeave={() => {
														setHoveredUserId(null);
													}}
												>
													{(userData?.user?.is_leader ||
														userData?.user?.is_manager) && (
														<>
															<span className="users-team-ellipsis">
																<ThreeDots size={24} color="#0052CC" />
															</span>
															{userData?.user?.is_leader && (
																<div className="users-team-promotion-popup">
																	<button
																		className="users-team-promotion-btn"
																		onClick={(e) => {
																			e.stopPropagation();
																			handlePromoteToManager(m.uid);
																		}}
																		disabled={promotingUserId === m.uid}
																	>
																		{promotingUserId === m.uid
																			? "در حال ارتقا..."
																			: "ارتقای کاربر به نقش مدیر"}
																	</button>
																</div>
															)}
															{userData?.user?.is_manager &&
																m.uid !== userData?.user?.uid &&
																!m.is_leader && (
																	<div className="users-team-promotion-popup">
																		<button
																			className="users-team-promotion-btn !tw-bg-red-500 hover:!tw-bg-red-100 hover:!tw-text-red-700"
																			onClick={(e) => {
																				e.stopPropagation();
																				handleRemoveUser(m);
																			}}
																		>
																			حذف کاربر
																		</button>
																	</div>
																)}
														</>
													)}
												</div>
											)}
										</div>
									</div>
								))}
							</MDBCardBody>
						</>
					)}
				</MDBCard>
			)}
			{/* AddTeamMember Modal */}
			{showAddMember && (
				<div className="add-team-member-modal-overlay">
					<div className="add-team-member-modal">
						<button
							className="add-team-member-modal-close"
							onClick={() => setShowAddMember(false)}
						>
							&times;
						</button>
						<AddTeamMember show={showAddMember} setShow={setShowAddMember} />
					</div>
				</div>
			)}
		</div>
	);
}

export default UsersInfo;
